<template><div>
	<v-container class="mt-6 mb-12">
		<div v-if="all_projects.length==0" class="text-center mb-5"><b>Welcome to the CASE Network GUID Translator tool.</b><br>Choose “New Project” below to get started with your first project.</div>
		<div class="text-center mb-5" :style="!show_dialog||'visibility:hidden'"><v-btn @click="initiate_new_project" color="primary" large><v-icon class="mr-2">fas fa-plus</v-icon> New Project</v-btn></div>

		<v-row>
			<Project v-for="(project, index) in all_projects"
				:key="project.project_id"
				:project="project"
				:project_index="index"
				v-show="project.created_at > 0 && !project.deleted"
				@edit_project_data="edit_project_data_start(index)"
			></Project>
		</v-row>
	</v-container>

	<v-dialog v-model="show_dialog" max-width="600" persistent>
		<v-card v-if="show_dialog">
			<v-card-text><v-container><v-row>
				<v-col cols="12">

					<v-text-field label="Project Name" hint="e.g. “Illinois ELA 2015”" v-model="pbe.project_name"></v-text-field>

					<v-text-field label="CASE Network Framework Identifier" hint="e.g. c64891a3-d7cb-11e8-824f-0242ac160002" v-model="pbe.case_framework_guid">
						<template v-slot:append-outer><v-btn @click="show_help('case-identifier-instructions')" fab small color="primary"><v-icon>fas fa-info</v-icon></v-btn></template>
					</v-text-field>

					<v-checkbox v-show="pbe.case_framework_cached_at>0" class="ml-8 mt-0" v-model="fetch_case_framework" label="Refresh CASE Network Framework"></v-checkbox>

					<v-text-field v-if="!pbe.upload_new_file" label="Database Upload File" v-model="pbe.db_filename" readonly>
						<template v-slot:append-outer><v-tooltip bottom>
							<template v-slot:activator="{on}"><v-btn v-on="on" @click="pbe.upload_new_file=true" fab small color="primary"><v-icon>fas fa-recycle</v-icon></v-btn></template>
							<span>Choose a new database upload file</span>
						</v-tooltip></template>
					</v-text-field>

					<v-file-input v-if="pbe.upload_new_file" ref="db_upload_input" label="Database Upload File (csv/tsv)" v-model="db_upload_input">
						<template v-slot:append-outer><v-btn @click="show_help('import-file-instructions')" fab small color="primary"><v-icon>fas fa-info</v-icon></v-btn></template>
					</v-file-input>

				</v-col>
			</v-row></v-container></v-card-text>
			<v-card-actions>
				<v-btn color="secondary" @click="edit_project_data_cancel">Cancel</v-btn>
				<v-spacer></v-spacer>
				<v-btn v-show="pbe.created_at>0" color="red" dark @click="delete_project">Delete Project</v-btn>
				<v-btn color="primary" @click="edit_project_data_save">Save</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<div id="k-case-identifier-instructions" style="display:none">
		<p>Enter the Identifier for the CASE Network Framework you want to align your database’s standards to. This identifier will be a 32-character GUID, e.g. “c6489520-d7cb-11e8-824f-0242ac160002” for the Illinois Learning Standards in Math. To find this identifier, go to the CASE Network website, locate and view the framework document, then copy the Identifier from the right side of the page (see screenshot below).</p>
		<div class="text-center"><img src="images/case_network_identifier.png" style="width:600px" class="elevation-3"></div>
		<p class="mt-2">Click the button below to open the CASE Network in a new tab.
		<p class="text-center"><v-btn href="https://casenetwork.imsglobal.org/cfdoc/" target="_blank" color="secondary">Open CASE Network</v-btn></p>
	</div>

	<div id="k-import-file-instructions" style="display:none">
		<p>To initiate the CASE GUID Translation process, you must upload a CSV- (comma-separated values) or TSV- (tab-separated values) formatted file that specifies how the standards framework is currently represented in your database. This file should include one line for each standard, along with a header line, and may include the following columns:</p>
		<ul>
			<li><b>STANDARD_ID:</b> (required) The ID your system uses to refer to the standard. This is typically an auto-incremented integer value, though some systems may use internally-generated GUIDs. Note that this is typically <i>not</i> the ID your system would currently use to communicate about the standard with another institution’s system.</li>
			<li><b>TEXT:</b> (required) The text of the standard, e.g. “Ask and answer questions about unknown words in a text.”</li>
			<li><b>CODE:</b> (optional) The “human readable code” for the standard, e.g. “RL.K.4”.</li>
			<li><b>GRADE_LOW:</b> (optional) The lower value of the grade range the standard applies to. If the standard applies to a single grade, GRADE_LOW and GRADE_HIGH should be the same value. Acceptable values are “K” or any integer 1 or higher.</li>
			<li><b>GRADE_HIGH:</b> (optional) The upper value of the grade range the standard applies to. If the standard applies to a single grade, GRADE_LOW and GRADE_HIGH should be the same value. Acceptable values are “K” or any integer 1 or higher.</li>
			<li><b>ALIGNABLE:</b> (optional) “1” if your institution allows resource objects to be aligned to the standard, or “0” otherwise.</li>
			<li><b>LEAF:</b> (optional) “1” if the standard is a “leaf” in the standard’s tree structure, or “0” if the standard is a “node”. Nodes are “parents” of other standards in the tree; leafs have no “children”.</li>
		</ul>
		<p class="mt-3">The header row must specify the columns included in the file, using the exact column names specified above (however, the columns can appear in any order).  Click the buttons below to open a Google Document with these instructions (which you can share with others), or download a sample file in TSV format.</p>
		<p class="text-center">
			<v-btn href="https://docs.google.com/document/d/12uiwQp-5PBXWjya0zNFwOtJ-hOhQHvV4OylKRzGTR1E" target="_blank" color="secondary" class="mr-2">Open Google Doc</v-btn>
			<v-btn href="./sample_db_upload_file.tsv" target="_blank" color="secondary">Download Sample File</v-btn>
		</p>
	</div>
</div></template>

<script>
import { mapState, mapGetters } from 'vuex'
import Project from './Project'

export default {
	components: { Project },
	data() { return {
		pbe: null,	// project_being_edited
		show_dialog: false,
		db_upload_input: [],
		fetch_case_framework: false,
	}},
	computed: {
		...mapState(['all_projects']),
	},
	created() {

	},
	mounted() {
	},
	methods: {
		initiate_new_project() {
			this.$store.commit('create_project')
			this.edit_project_data_start(0)
		},

		edit_project_data_start(which) {
			if (typeof(which) != 'number') {
			}
			this.pbe = {
				index: which,
				project_name: this.all_projects[which].project_name,
				case_framework_title: this.all_projects[which].case_framework_title,
				case_framework_guid: this.all_projects[which].case_framework_guid,
				case_framework_cached_at: this.all_projects[which].case_framework_cached_at,
				db_filename: this.all_projects[which].db_filename,
				db_uploaded_at: this.all_projects[which].db_uploaded_at,
				created_at: this.all_projects[which].created_at,
				upload_new_file: (this.all_projects[which].db_filename == ''),
			}
			this.show_dialog = true
		},

		edit_project_data_cancel() {
			console.log(this.pbe)
			// if project was never saved, delete it
			if (this.pbe.created_at == 0) {
				this.$store.commit('delete_project', this.pbe.index)
			}
			this.fetch_case_framework = false
			this.db_upload_input = []
			this.pbe = null
			this.show_dialog = false
		},

		edit_project_data_save(e) {
			this.pbe.project_name = $.trim(this.pbe.project_name)

			if (empty(this.pbe.project_name)) {
				this.$alert('You must specify a Project Name to initiate a new project.')
				return
			}

			let o = {
				pbe: this.pbe,
				fetch_case_framework: this.fetch_case_framework
			}

			// if we got a file, include it
			if (!empty(this.db_upload_input) && !empty(this.db_upload_input.name)) {
				// note that although db_upload_input is defined as an array, since we only currently allow for one file to be selected, only a single file object is returned.

				// note the filename
				o.db_filename = this.db_upload_input.name
				o.db_upload_input = this.db_upload_input
			}

			// if user held down shift key when clicking save, always recalculate sim scores
			o.recalculate_sim_scores = (!empty(e) && e.shiftKey) + ''

			this.$store.dispatch('save_project_data', o).then(()=>{
				// on successful save, clear form and close dialog
				this.fetch_case_framework = false
				this.db_upload_input = []
				this.pbe = null
				this.show_dialog = false

			}).catch((error)=>{
				// on error, alert te error and re-call edit_project_data_start so that we refresh with any saved project data
				this.$alert(error)
				this.edit_project_data_start(this.pbe.index)
			})
		},

		delete_project() {
			this.$confirm({
			    title: 'Confirm Delete',
			    text: sr('Are you sure you want to delete project “$1”?', this.pbe.project_name),
			    acceptText: 'Delete',
				acceptColor: 'red',
			}).then(y => {
				let index = this.pbe.index
				this.pbe.index = null
				this.show_dialog = false
				this.fetch_case_framework = false
				this.db_upload_input = []
				this.$store.commit('delete_project', index)
			}).catch(n=>{console.log(n)}).finally(f=>{});
		},

		show_help(id) {
			let html = $('#k-' + id).html()
			this.$alert({text:html, dialogMaxWidth:800})
		},
	}
}
</script>

<style lang="scss">
</style>
